import { createAsyncThunk } from "@reduxjs/toolkit";
import { checkStatus, composeAuth, AuthError } from "../../helpers/utils";
import { logoutUser } from "./account";

const CONSTRUCTION_LIST_MAX_LIMIT = 200;
const CONSTRUCTION_MATRIX_MAX_LIMIT = 1000;

const normalizeConstructionLimit = (limit, maxLimit = CONSTRUCTION_LIST_MAX_LIMIT) => {
  if (limit === undefined || limit === null || limit === "") {
    return limit;
  }

  const numericLimit = Number(limit);
  if (Number.isNaN(numericLimit)) {
    return limit;
  }

  return Math.min(numericLimit, maxLimit);
};

const buildQueryString = (payload = {}) => {
  const query = new URLSearchParams();

  ["offset", "limit", "search", "move_required", "unitId", "householdId", "dateFrom", "dateTo", "includeTemporaryAssignments"].forEach((key) => {
    if (payload?.[key] !== undefined && payload?.[key] !== null && payload?.[key] !== "") {
      query.set(key, key === "limit" ? normalizeConstructionLimit(payload[key]) : payload[key]);
    }
  });

  return query.toString();
};

const buildPaginationQueryString = (payload = {}, maxLimit = CONSTRUCTION_LIST_MAX_LIMIT) => {
  const query = new URLSearchParams();

  ["offset", "limit"].forEach((key) => {
    if (payload?.[key] !== undefined && payload?.[key] !== null && payload?.[key] !== "") {
      query.set(key, key === "limit" ? normalizeConstructionLimit(payload[key],maxLimit) : payload[key]);
    }
  });

  return query.toString();
};

const buildConstructionsFilterPayload = (payload = {}) => {
  if (payload?.body) {
    return payload.body;
  }

  const body = {};

  if (payload?.title) {
    body.title = payload.title;
  } else if (payload?.search) {
    body.title = {
      data: payload.search,
      action: "include",
    };
  }

  if (payload?.sort) {
    body.sort = payload.sort;
  }

  ["move_required", "unitId", "householdId", "dateFrom", "dateTo", "includeTemporaryAssignments"].forEach((key) => {
    if (payload?.[key] !== undefined && payload?.[key] !== null && payload?.[key] !== "") {
      body[key] = payload[key];
    }
  });

  return body;
};

const buildConstructionMatrixPayload = (payload = {}, includeVisibleColumnIds = false) => {
  const body = {
    title: payload?.search ? { data: payload.search, action: "include" } : payload?.title || undefined,
    move_required: payload?.move_required || undefined,
    cohortId: payload?.cohortId || undefined,
    cohort: payload?.cohort || undefined,
    sort: payload?.sort || undefined,
    dateFrom: payload?.dateFrom || undefined,
    dateTo: payload?.dateTo || undefined,
    fieldFilters: payload?.fieldFilters || {},
    aiSearch: payload?.aiSearch || undefined,
  };

  if (includeVisibleColumnIds) {
    body.visibleColumnIds = payload?.visibleColumnIds || [];
  }

  return body;
};

export const getConstructionsByInstitution = createAsyncThunk(
  "construction/getConstructionsByInstitution",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const query = buildQueryString(payload);
      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/institution/${payload?.institutionId}${query ? `?${query}` : ""}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionById = createAsyncThunk(
  "construction/getConstructionById",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/constructions/${payload?.id}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionLogs = createAsyncThunk(
  "construction/getConstructionLogs",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const query = new URLSearchParams();

      if (payload?.offset !== undefined && payload?.offset !== null) {
        query.set("offset", payload.offset);
      }
      if (payload?.limit !== undefined && payload?.limit !== null) {
        query.set("limit", payload.limit);
      }

      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/${payload?.id}/logs${query.toString() ? `?${query.toString()}` : ""}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        signal: thunkApi.signal,
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionLogsByIds = createAsyncThunk(
  "construction/getConstructionLogsByIds",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + "/api/constructions/logs/search";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify({
          constructionIds: payload?.constructionIds || [],
          offset: payload?.offset || 0,
          limit: payload?.limit || 20,
        }),
        signal: thunkApi.signal,
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getMultipleConstructionSummaryById = createAsyncThunk(
  "construction/getMultipleConstructionSummaryById",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + "/api/constructions/multiple-construction-summary-by-id";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify({
          constructionIds: payload?.constructionIds || [],
        }),
        signal: thunkApi.signal,
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionsByInstitutionPatch = createAsyncThunk(
  "construction/getConstructionsByInstitutionPatch",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const query = buildPaginationQueryString(payload);
      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/institution/${payload?.institutionId}${query ? `?${query}` : ""}`;

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(buildConstructionsFilterPayload(payload)),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const addConstruction = createAsyncThunk(
  "construction/addConstruction",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/constructions`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(payload?.data),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const editConstruction = createAsyncThunk(
  "construction/editConstruction",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/constructions/${payload?.id}`;

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(payload?.data),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const deleteConstruction = createAsyncThunk(
  "construction/deleteConstruction",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/constructions/${payload?.id}`;

      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const bulkUploadConstructions = createAsyncThunk(
  "construction/bulkUploadConstructions",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/constructions/bulk-csv`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(payload?.data),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const bulkUpdateConstructions = createAsyncThunk(
  "construction/bulkUpdateConstructions",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/constructions/bulk-update-csv`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(payload?.data),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionsForExport = createAsyncThunk(
  "construction/getConstructionsForExport",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/institution/${payload?.institutionId}/csv-download`;

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(buildConstructionsFilterPayload(payload)),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getVacantUnitUtilization = createAsyncThunk(
  "construction/getVacantUnitUtilization",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const query = new URLSearchParams();

      ["dateFrom", "dateTo"].forEach((key) => {
        if (payload?.[key]) {
          query.set(key, payload[key]);
        }
      });

      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/institution/${payload?.institutionId}/vacant-unit-utilization${query.toString() ? `?${query.toString()}` : ""}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionSummaryReport = createAsyncThunk(
  "construction/getConstructionSummaryReport",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/institution/${payload?.institutionId}/summary-report`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionMatrixByInstitution = createAsyncThunk(
  "construction/getConstructionMatrixByInstitution",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const query = buildPaginationQueryString(payload, CONSTRUCTION_MATRIX_MAX_LIMIT);

      const url = process.env.REACT_APP_SERVER + `/api/constructions/institution/${payload?.institutionId}/matrix${query ? `?${query}` : ""}`;

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(buildConstructionMatrixPayload(payload)),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionMatrixByInstitutionWithAiSearch = createAsyncThunk(
  "construction/getConstructionMatrixByInstitutionWithAiSearch",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const query = buildPaginationQueryString(payload, CONSTRUCTION_MATRIX_MAX_LIMIT);

      const url = process.env.REACT_APP_SERVER + `/api/constructions/institution/${payload?.institutionId}/matrix/ai-search${query ? `?${query}` : ""}`;

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify({
          prompt: payload?.prompt,
          filters: buildConstructionMatrixPayload(payload),
        }),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getConstructionMatrixForExport = createAsyncThunk(
  "construction/getConstructionMatrixForExport",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/institution/${payload?.institutionId}/matrix/csv-download`;

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(buildConstructionMatrixPayload(payload, true)),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const bulkEditConstructionMatrix = createAsyncThunk(
  "construction/bulkEditConstructionMatrix",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url =
        process.env.REACT_APP_SERVER +
        `/api/constructions/institution/${payload?.institutionId}/matrix/bulk-edit`;
      const body = {
        scope: payload?.scope,
        constructionIds: payload?.constructionIds || [],
        filters: buildConstructionMatrixPayload(payload?.filters || {}),
        updates: payload?.updates || [],
      };

      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization,
        },
        body: JSON.stringify(body),
      });

      const response = await res.json();
      return checkStatus(response);
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);
